import { Link, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, FileText, DollarSign, Settings, LogOut, Briefcase, Package, ClipboardList, UserCheck } from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  useSidebar,
} from "@/components/ui/sidebar";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { useAdmin } from "@/hooks/useAdmin";


const menuItems = [
  { title: "Dashboard", url: "/admin", icon: LayoutDashboard, enabled: true },
  { title: "Usuários", url: "/admin/usuarios", icon: Users, enabled: true },
  { title: "Vendedores", url: "/admin/vendedores", icon: Briefcase, enabled: true },
  { title: "Produtos/Sistemas", url: "/admin/produtos", icon: Package, enabled: true },
  { title: "Leads", url: "/admin/leads", icon: ClipboardList, enabled: true },
  { title: "Clientes", url: "/admin/clientes", icon: UserCheck, enabled: true },
  { title: "Financeiro", url: "/admin/financeiro", icon: DollarSign, enabled: true },
  { title: "Contas", url: "/admin/contas", icon: FileText, enabled: true },
  { title: "Configurações", url: "/admin/configuracoes", icon: Settings, enabled: true },
];

export function AdminSidebar() {
  const location = useLocation();
  const { state } = useSidebar();
  const { signOut } = useAuth();
  const { data: adminData } = useAdmin();
  
  const isCollapsed = state === "collapsed";

  return (
    <Sidebar collapsible="icon" className="border-r border-border bg-card">
      <SidebarHeader className="border-b border-border px-4 py-4 bg-card">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 shrink-0 rounded-md bg-primary flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-sm">DI</span>
          </div>
          {!isCollapsed && (
            <div className="flex flex-col">
              <span className="font-bold text-lg">Digital Ideia</span>
              <span className="text-xs text-muted-foreground">Painel Admin</span>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="bg-card">
        <SidebarGroup>
          {!isCollapsed && <SidebarGroupLabel>Menu Principal</SidebarGroupLabel>}
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => {
                const isActive = location.pathname === item.url;
                const Icon = item.icon;

                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive}
                      disabled={!item.enabled}
                      tooltip={item.title}
                      size="lg"
                      className={cn(
                        !item.enabled ? "opacity-40 cursor-not-allowed" : "",
                        isCollapsed && "flex items-center justify-center"
                      )}
                    >
                      <Link to={item.enabled ? item.url : "#"}>
                        <Icon className="h-5 w-5 shrink-0" />
                        {!isCollapsed && <span className="text-sm">{item.title}</span>}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-border bg-card">
        <div className="p-4 space-y-3">
          {!isCollapsed && (
            <>
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    {adminData?.name?.charAt(0)?.toUpperCase() || "A"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col flex-1 min-w-0">
                  <span className="text-sm font-medium truncate">
                    {adminData?.name || "Administrador"}
                  </span>
                  <span className="text-xs text-muted-foreground truncate">
                    {adminData?.email || "admin@digitalideia.com"}
                  </span>
                </div>
              </div>
              <Separator />
            </>
          )}
          <Button
            variant="ghost"
            size={isCollapsed ? "icon" : "sm"}
            className="w-full justify-start"
            onClick={() => signOut()}
          >
            <LogOut className="h-4 w-4" />
            {!isCollapsed && <span className="ml-2">Sair</span>}
          </Button>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
